import React from 'react';
import { Link } from 'react-router-dom';
import { TreePine, ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <TreePine className="w-8 h-8 text-primary" strokeWidth={1.5} />
            <h1 className="text-2xl font-serif font-semibold text-foreground">Sarmoh</h1>
          </Link>
          <Link to="/" className="text-primary hover:underline font-sans text-sm flex items-center">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-serif text-foreground mb-8">Privacy Policy</h1>
        
        <div className="prose prose-gray max-w-none">
          <p className="text-foreground-muted mb-6">
            <strong>Last Updated:</strong> February 2026
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">1. Introduction</h2>
            <p className="text-foreground-muted leading-relaxed">
              TLML Mediatech Pvt Ltd ("we", "our", or "us") operates Sarmoh.in. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-serif text-foreground mb-3">Personal Information</h3>
            <p className="text-foreground-muted leading-relaxed mb-4">
              We may collect personal information that you provide directly, including:
            </p>
            <ul className="list-disc pl-6 text-foreground-muted space-y-2 mb-4">
              <li>Name and email address</li>
              <li>Profile pictures</li>
              <li>Family member information you choose to share</li>
              <li>Tribute messages and voice recordings</li>
              <li>Photos and other media you upload</li>
            </ul>
            
            <h3 className="text-xl font-serif text-foreground mb-3">Automatically Collected Information</h3>
            <p className="text-foreground-muted leading-relaxed">
              When you access our Service, we automatically collect certain information including:
            </p>
            <ul className="list-disc pl-6 mt-3 text-foreground-muted space-y-2">
              <li>Device information and browser type</li>
              <li>IP address and location data</li>
              <li>Usage patterns and preferences</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">3. How We Use Your Information</h2>
            <p className="text-foreground-muted leading-relaxed">We use the collected information to:</p>
            <ul className="list-disc pl-6 mt-3 text-foreground-muted space-y-2">
              <li>Provide and maintain our Service</li>
              <li>Create and manage your account</li>
              <li>Enable family tree and memorial features</li>
              <li>Send important updates and notifications</li>
              <li>Improve and personalize your experience</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">4. Information Sharing</h2>
            <p className="text-foreground-muted leading-relaxed">
              We do not sell your personal information. We may share your information in these situations:
            </p>
            <ul className="list-disc pl-6 mt-3 text-foreground-muted space-y-2">
              <li><strong>With your consent:</strong> When you explicitly agree to share information</li>
              <li><strong>For legal compliance:</strong> When required by law or legal process</li>
              <li><strong>Service providers:</strong> With trusted third parties who assist in operating our Service</li>
              <li><strong>Business transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">5. Data Security</h2>
            <p className="text-foreground-muted leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your 
              personal information. However, no method of transmission over the Internet is 100% secure, 
              and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">6. Data Retention</h2>
            <p className="text-foreground-muted leading-relaxed">
              We retain your personal information for as long as necessary to provide our Service and 
              fulfill the purposes outlined in this Privacy Policy. Memorial and family tree data may 
              be retained indefinitely unless you request deletion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">7. Your Rights</h2>
            <p className="text-foreground-muted leading-relaxed">You have the right to:</p>
            <ul className="list-disc pl-6 mt-3 text-foreground-muted space-y-2">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to certain processing activities</li>
              <li>Export your data in a portable format</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">8. Cookies</h2>
            <p className="text-foreground-muted leading-relaxed">
              We use cookies and similar technologies to enhance your experience, analyze usage, and 
              provide personalized content. You can control cookie preferences through your browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">9. Children's Privacy</h2>
            <p className="text-foreground-muted leading-relaxed">
              Our Service is not intended for children under 13. We do not knowingly collect personal 
              information from children under 13. If you believe we have collected such information, 
              please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">10. Changes to This Policy</h2>
            <p className="text-foreground-muted leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-serif text-foreground mb-4">11. Contact Us</h2>
            <p className="text-foreground-muted leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at:<br />
              <strong>TLML Mediatech Pvt Ltd</strong><br />
              Email: privacy@sarmoh.in
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 bg-paper border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm font-sans text-muted-foreground">
            © 2026 TLML Mediatech Pvt Ltd. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
